import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { usePagination } from '@abyss/web/hooks/usePagination';
import { storage } from '@abyss/web/tools/storage';
import { Divider } from '@abyss/web/ui/Divider';
import { Drawer } from '@abyss/web/ui/Drawer';
import { Flex } from '@abyss/web/ui/Flex';
import { LoadingSpinner } from '@abyss/web/ui/LoadingSpinner';
import { Popover } from '@abyss/web/ui/Popover';
import { find } from 'lodash';
import { useEffect, useRef, useState } from 'react';

import {
  DataCardList,
  DataCardListStyled,
} from '../../frontends/ProviderSearch/routes/MapView/DataCardList';
import { MapViewTopPagination } from '../../frontends/ProviderSearch/routes/MapView/MapViewTopPagination';
import { useLagoon } from '../../hooks/useLagoon';
import { useViewAllData } from '../../hooks/useViewAllData';
import { Constants } from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { mediumScreen, mobileOnly } from '../ConstantsStyles';
import { GroupedLocationDrawerHeaderStyle } from '../DataCard/DataCard.style';
import { DataCardContainer } from '../DataCard/DataCardContainer';
import { getFeatureFlag } from '../Utils';

type Props = {
  openDrawer: boolean;
  setOpenDrawer: (open: boolean) => void;
  drawerContent: any;
  pageNumber: any;
  pageSize: any;
  coverageType: any;
  shouldGetHGData: any;
  navigatedFromDetails: boolean;
  setNavigatedFromDetails: (navigated: boolean) => void;
  providerToScrollTo?: string;
  isMNR?: boolean;
  isTieredPlan?: boolean;
  searchRadius?: any;
  recommendationPreferences?: any;
};

export const MapViewGroupedLocationDrawer = ({
  openDrawer = false,
  setOpenDrawer,
  drawerContent,
  pageNumber,
  pageSize,
  coverageType,
  shouldGetHGData,
  navigatedFromDetails,
  setNavigatedFromDetails,
  providerToScrollTo = '',
  isMNR,
  isTieredPlan,
  searchRadius,
  recommendationPreferences,
}: Props) => {
  const topPaginationRef = useRef<HTMLDivElement>(null);
  const drawerContentPaginationRef = useRef<HTMLDivElement>(null);
  const lagoonData = useLagoon('ui-messaging')();
  const featureFlags: [{ key: string; active: boolean }] =
    useLagoon('feature-flags')();
  const enableMapViewEnhancements: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_LIST_VIEW_MAP_ENHANCEMENTS
  );
  const groupResultsMessaging = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.GROUP_RESULTS_MESSAGING,
  });
  const medScreen = useMediaQuery(mediumScreen);
  const smallScreen = useMediaQuery(mobileOnly);
  const bottomDrawerPositionScreen = useMediaQuery('(max-width: 400px)');
  const totalAllResultsCount = drawerContent?.numberProviders ?? 0;
  const totalPages = Math.ceil(totalAllResultsCount / pageSize);
  const [isOpen, setIsOpen] = useState(openDrawer);
  const [page, setPage] = useState(pageNumber ?? 1);
  const providerLocationIds = drawerContent?.locationId?.map((id: string) =>
    id
      ?.split('-')
      ?.filter((_, index, arr) => index === 0 || index === arr.length - 1)
      ?.join('-')
  );
  const providerLocationId = providerLocationIds?.join(',') ?? '';

  const {
    results: providerResult,
    isLoading,
    sectionType,
  } = useViewAllData({
    pageNumber: page,
    pageSize,
    coverageType,
    shouldGetHGData,
    mapLocationsOnly: false,
    providerLocationId,
    extendedPinsDrawerReq: true,
  });

  const { state, gotoPage, nextPage, previousPage, ...paginationProps } =
    usePagination({
      pages: totalPages,
      start: pageNumber,
    });

  const { currentPage } = state;

  const gotoPageFunc = (page) => {
    gotoPage(page);
    topPaginationRef?.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  useEffect(() => {
    setPage(currentPage);
    drawerContentPaginationRef?.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, [currentPage]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (navigatedFromDetails) {
        const elements = document.querySelectorAll(
          `[data-auto-testid="data-card-heading-${sectionType}-${providerToScrollTo}"]`
        );
        const element = elements[0];
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setNavigatedFromDetails(false);
          storage.session.remove(
            Constants.STORAGE_KEYS.SESSION.LAST_SELECTED_FROM_DRAWER
          );
          clearInterval(intervalId);
        }
      } else {
        clearInterval(intervalId);
      }
    }, 100);
  }, []);

  return (
    <Drawer
      closeOnClickOutside={false}
      css={GroupedLocationDrawerHeaderStyle}
      isOpen={isOpen}
      onClose={() => {
        setIsOpen(false);
        setOpenDrawer(false);
      }}
      position={bottomDrawerPositionScreen ? 'bottom' : 'right'}
      size={medScreen ? '568px' : smallScreen ? '356px' : '784px'}
      title={
        <Flex
          css={{
            fontWeight: '700',
            fontSize: '22.78px',
            lineHeight: '28px',
            letterSpacing: '0px',
            color: '#323333',
            gap: '4px',
            '@media (max-width: 743px)': {
              paddingTop: '20px',
              fontSize: '18px',
            },
          }}
        >
          {`${totalAllResultsCount} ${groupResultsMessaging?.title || ''}`}
          <Popover
            content={groupResultsMessaging?.message || ''}
            width={'339px'}
          />
        </Flex>
      }
    >
      {Object.keys(providerResult).length === 0 ? (
        <LoadingSpinner
          ariaLoadingLabel="loading results"
          isLoading={isLoading}
          size="$sm"
        />
      ) : (
        <div ref={drawerContentPaginationRef}>
          <Drawer.Section
            css={{
              padding: '8px 0 0 0',
              position: 'sticky',
              top: '0',
              backgroundColor: 'white',
              zIndex: '10',
            }}
          >
            <Divider
              color="#E5E5E6"
              css={{ width: '784px' }}
              height={1}
              margin={0}
            />
          </Drawer.Section>
          <Drawer.Section>
            {isLoading ? (
              <DataCardListStyled style={{ marginTop: '18px' }}>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((item) => (
                  <DataCardContainer
                    isGroupedLocationDrawer={true}
                    isLoading={isLoading}
                    isMNR={isMNR}
                    key={item}
                  />
                ))}
              </DataCardListStyled>
            ) : (
              <DataCardList
                DataCardStyle={{
                  width: '408px',
                  minHeight: '100px',
                }}
                currentPage={currentPage}
                drawerContent={drawerContent}
                expandedProviderListView={false}
                fromMobileListView={false}
                heading={'results'}
                isGroupedLocationDrawer={true}
                isLoading={false}
                isMNR={isMNR}
                isMobileMap={false}
                isTieredPlan={isTieredPlan}
                locationForAnalytics={`results for `}
                pageNumber={1}
                providerResults={providerResult || []}
                recommendationPreferences={recommendationPreferences}
                searchRadius={searchRadius}
                sectionType={sectionType}
                selectedCheckbox={{ checked: {} }}
                showCheckboxes={false}
              />
            )}
            {totalPages > 1 && (
              <MapViewTopPagination
                enableMapViewEnhancements={enableMapViewEnhancements}
                enableMapViewHideOption={true}
                expandedProviderListView={false}
                gotoPages={gotoPageFunc}
                isGroupedLocationDrawer={true}
                isLoading={isLoading}
                nextPageTop={nextPage}
                pageSize={pageSize}
                pageSizeOptions={[50]}
                paginationProps={paginationProps}
                previousPageTop={previousPage}
                topPaginationRef={topPaginationRef}
              />
            )}
          </Drawer.Section>
        </div>
      )}
    </Drawer>
  );
};
